import React, { useState, useRef, useEffect } from 'react';

const Bot3DViewer = ({ viewerPort, isConnected, isViewerReady, onSendControl, onSendLook, onOpenMenu }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPointerLocked, setIsPointerLocked] = useState(false);
  const [fps, setFps] = useState(60);
  const [iframeKey, setIframeKey] = useState(0);
  const [isReadyToRender, setIsReadyToRender] = useState(false);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const yawRef = useRef(0);
  const pitchRef = useRef(0);
  const lastLookSentRef = useRef(0);

  // Reload iframe automatically whenever viewer becomes ready on backend
  useEffect(() => {
    if (isViewerReady) {
      setIsReadyToRender(true);
      setIframeKey(Date.now());
    } else if (!isConnected) {
      setIsReadyToRender(false);
      setIframeKey(0);
    }
  }, [isViewerReady, isConnected]);

  // Fallback timer: if connected but isViewerReady event was missed or already emitted before mount
  useEffect(() => {
    if (isConnected && !isViewerReady && !isReadyToRender) {
      const fallbackTimer = setTimeout(() => {
        setIsReadyToRender(true);
        setIframeKey(Date.now());
      }, 2500);
      return () => clearTimeout(fallbackTimer);
    }
  }, [isConnected, isViewerReady, isReadyToRender]);

  useEffect(() => {
    const handlePointerLockChange = () => {
      const isLocked = document.pointerLockElement === containerRef.current;
      setIsPointerLocked(isLocked);
    };

    const handleMouseMove = (e) => {
      if (document.pointerLockElement !== containerRef.current) return;

      const sensitivity = 0.0032;
      yawRef.current -= (e.movementX || 0) * sensitivity;
      pitchRef.current = Math.max(
        -Math.PI / 2 + 0.01,
        Math.min(Math.PI / 2 - 0.01, pitchRef.current - (e.movementY || 0) * sensitivity)
      );

      const now = performance.now();
      if (now - lastLookSentRef.current >= 30) {
        lastLookSentRef.current = now;
        if (onSendLook) {
          onSendLook(yawRef.current, pitchRef.current);
        }
      }
    };

    document.addEventListener('pointerlockchange', handlePointerLockChange);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [onSendLook]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      // Trigger resize after layout animation
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen().catch(() => {});
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  const handleRequestPointerLock = () => {
    if (containerRef.current && !document.pointerLockElement) {
      containerRef.current.requestPointerLock();
    }
  };

  const handleReload = () => {
    setIsReadyToRender(true);
    setIframeKey(Date.now());
  };

  return (
    <div
      ref={containerRef}
      className={`relative rounded-2xl overflow-hidden border border-purple-500/20 bg-[#0c0c10] shadow-2xl transition-all ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none w-screen h-screen max-w-none' : 'w-full max-w-full h-[280px] sm:h-[380px] md:h-[480px]'
      }`}
      style={{ boxSizing: 'border-box' }}
    >
      {/* Top HUD Overlay */}
      <div className="absolute top-3 left-3 right-3 z-20 flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/70 backdrop-blur-md border border-emerald-500/30 text-emerald-400 text-xs font-mono font-semibold shadow-lg">
            <span className={`w-2 h-2 rounded-full ${isReadyToRender ? 'bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400' : 'bg-amber-400'}`}></span>
            {isReadyToRender ? 'LIVE 3D (1st Person)' : 'Подключение...'}
          </span>
          <span className="px-2.5 py-1.5 rounded-xl bg-black/70 backdrop-blur-md border border-purple-500/30 text-purple-300 text-xs font-mono">
            60 FPS
          </span>
        </div>

        <div className="flex items-center gap-2 pointer-events-auto">
          {onOpenMenu && (
            <button
              onClick={onOpenMenu}
              className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-medium backdrop-blur-md border border-purple-400/40 transition-all flex items-center gap-2 shadow-lg shadow-purple-900/30 active:scale-95 cursor-pointer"
              title="Открыть меню серверов"
            >
              <i className="fas fa-compass text-amber-300 text-xs"></i>
              <span>Меню [E]</span>
            </button>
          )}

          <button
            onClick={handleReload}
            className="p-2 rounded-xl bg-black/70 hover:bg-black/90 text-gray-300 hover:text-cyan-300 backdrop-blur-md border border-white/10 hover:border-cyan-500/30 transition-all shadow-lg active:scale-95 cursor-pointer"
            title="Перезагрузить 3D-поток"
          >
            <i className="fas fa-rotate-right text-xs"></i>
          </button>

          <a
            href="/viewer/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-xl bg-black/70 hover:bg-black/90 text-cyan-300 hover:text-white backdrop-blur-md border border-cyan-500/30 hover:border-cyan-400/50 transition-all text-xs flex items-center gap-1.5 shadow-lg active:scale-95"
            title="Открыть в новой вкладке"
          >
            <i className="fas fa-arrow-up-right-from-square text-[10px]"></i>
            <span className="hidden sm:inline">Новая вкладка</span>
          </a>

          <button
            onClick={handleToggleFullscreen}
            className="p-2 rounded-xl bg-black/70 hover:bg-black/90 text-gray-300 hover:text-purple-300 backdrop-blur-md border border-white/10 hover:border-purple-500/30 transition-all shadow-lg active:scale-95 cursor-pointer"
            title={isFullscreen ? 'Выйти из полноэкранного режима' : 'На весь экран'}
          >
            <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'} text-xs`}></i>
          </button>
        </div>
      </div>

      {/* Center Crosshair Overlay */}
      {isReadyToRender && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-10">
          <div className="relative w-5 h-5 flex items-center justify-center opacity-70">
            <div className="w-3.5 h-[1.5px] bg-white/90 absolute shadow-sm"></div>
            <div className="h-3.5 w-[1.5px] bg-white/90 absolute shadow-sm"></div>
            <div className="w-1 h-1 rounded-full bg-cyan-400 absolute"></div>
          </div>
        </div>
      )}

      {/* 3D Stream Viewport Iframe */}
      {isConnected && isReadyToRender && iframeKey > 0 ? (
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={`/viewer/?t=${iframeKey}`}
          title="Bot First-Person View"
          className="w-full h-full border-0"
          style={{ width: '100%', height: '100%', border: 0, display: 'block', maxWidth: '100%' }}
          allow="autoplay; fullscreen"
        />
      ) : isConnected ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-purple-300 gap-3 bg-[#0a0a10]">
          <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center animate-pulse shadow-lg shadow-purple-900/30">
            <i className="fas fa-satellite-dish text-2xl text-cyan-400"></i>
          </div>
          <p className="text-sm font-medium font-mono text-gray-200">Инициализация 3D-потока мира...</p>
          <span className="text-xs text-gray-500 font-mono">Запуск движка и синхронизация чанков</span>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-3 bg-[#0a0a10]">
          <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
            <i className="fas fa-cube text-2xl text-purple-400"></i>
          </div>
          <p className="text-sm font-medium text-gray-400">Ожидание подключения бота для запуска 3D-стрима...</p>
        </div>
      )}

      {/* Bottom Controls Info Banner */}
      <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between pointer-events-none text-[11px] text-gray-300">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/75 backdrop-blur-md border border-white/10 shadow-lg">
          <i className="fas fa-keyboard text-purple-400"></i>
          <span>Управление: <strong className="text-white font-mono bg-white/10 px-1.5 py-0.5 rounded text-[10px]">W A S D</strong> • Прыжок: <strong className="text-white font-mono bg-white/10 px-1.5 py-0.5 rounded text-[10px]">Space</strong> • Меню: <strong className="text-white font-mono bg-white/10 px-1.5 py-0.5 rounded text-[10px]">E</strong></span>
        </div>

        <button
          onClick={handleRequestPointerLock}
          className={`pointer-events-auto px-3 py-1.5 rounded-xl backdrop-blur-md border transition-all text-xs flex items-center gap-1.5 shadow-lg active:scale-95 cursor-pointer ${
            isPointerLocked
              ? 'bg-emerald-600/80 border-emerald-400/50 text-white shadow-emerald-900/30'
              : 'bg-black/75 hover:bg-purple-900/40 border-purple-500/30 text-purple-300 hover:text-white'
          }`}
        >
          <i className={`fas ${isPointerLocked ? 'fa-lock' : 'fa-mouse'} ${isPointerLocked ? 'text-white' : 'text-cyan-400'}`}></i>
          <span>{isPointerLocked ? 'Мышь захвачена (ESC для выхода)' : 'Захват мыши'}</span>
        </button>
      </div>
    </div>
  );
};

export default Bot3DViewer;
